/**
 * CatalogContext: equipos + láminas cargados una sola vez al iniciar sesión.
 * Son ~1000 filas estáticas, no necesitan re-fetch.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchStickers, fetchTeams, type Sticker, type Team } from "../api/catalog";
import { useAuth } from "./AuthContext";

interface CatalogValue {
  teams: Team[];
  stickers: Sticker[];
  loading: boolean;
  error: string | null;
  teamById: (id: number) => Team | undefined;
  teamByCode: (code: string) => Team | undefined;
  stickerById: (id: number) => Sticker | undefined;
  stickersByTeamId: (teamId: number) => Sticker[];
}

const CatalogContext = createContext<CatalogValue | null>(null);

export function CatalogProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [stickers, setStickers] = useState<Sticker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== "authenticated") {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([fetchTeams(), fetchStickers()])
      .then(([t, s]) => {
        setTeams(t);
        setStickers(s);
        setError(null);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [status]);

  const value = useMemo<CatalogValue>(() => {
    const teamMap = new Map(teams.map((t) => [t.id, t]));
    const teamCodeMap = new Map(teams.map((t) => [t.code, t]));
    const stickerMap = new Map(stickers.map((s) => [s.id, s]));
    const byTeam = new Map<number, Sticker[]>();
    for (const s of stickers) {
      const list = byTeam.get(s.team_id) ?? [];
      list.push(s);
      byTeam.set(s.team_id, list);
    }
    return {
      teams,
      stickers,
      loading,
      error,
      teamById: (id) => teamMap.get(id),
      teamByCode: (code) => teamCodeMap.get(code),
      stickerById: (id) => stickerMap.get(id),
      stickersByTeamId: (id) => byTeam.get(id) ?? [],
    };
  }, [teams, stickers, loading, error]);

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog(): CatalogValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error("useCatalog() debe estar dentro de <CatalogProvider>");
  return ctx;
}
